import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEntities, useAllEntities } from '@/hooks';
import type { Entity } from '@/types/Entity';
import {
  parseBulkPersonSizingImportFile,
  normalizeIndividualId,
} from '@/components/iem/dialogs/personBulkSizingImport';
import {
  personSizingService,
  type PersonSizingAssignmentInput,
} from '@/services/iem/PersonSizingService';
import { useNotifications } from '@/stores/useNotificationStore';
import { logger } from '@equip/utils/logger';
import { getErrorMessage } from '@/utils/httpClient';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { usePersonLabels } from '@/hooks/usePersonLabels';

const log = logger.child({ component: 'PersonBulkSizingImportDialog' });
const PERSON_BULK_SIZING_TEMPLATE_PATH = '/templates/person-bulk-sizing-template.csv';
const PERSON_BULK_SIZING_DEMO_PATH = '/templates/person-bulk-sizing-demo.csv';

interface PersonBulkSizingImportDialogProps {
  open: boolean;
  onClose: () => void;
}

interface PersonEntityData {
  individual_id?: string;
}

interface ResolvedPersonImport {
  person: Entity;
  individualId: string;
  assignments: PersonSizingAssignmentInput[];
}

const runInBatches = async <T,>(
  items: T[],
  batchSize: number,
  worker: (item: T) => Promise<void>,
): Promise<void> => {
  for (let index = 0; index < items.length; index += batchSize) {
    const batch = items.slice(index, index + batchSize);
    await Promise.all(batch.map((item) => worker(item)));
  }
};

export const PersonBulkSizingImportDialog: React.FC<PersonBulkSizingImportDialogProps> = ({
  open,
  onClose,
}) => {
  const notifications = useNotifications();
  const person = usePersonLabels('front_window');
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [resolvedImports, setResolvedImports] = useState<ResolvedPersonImport[]>([]);

  const {
    data: catalogItems = [],
    isLoading: catalogLoading,
    error: catalogError,
  } = useAllEntities({
    type: 'catalog',
    filters: { status: 'eq.active' },
    enabled: open,
  });

  const {
    data: people = [],
    isLoading: peopleLoading,
    error: peopleError,
  } = useEntities({
    type: 'person',
    filters: { 'entity_data->>individual_id': 'not.is.null', status: 'eq.active' },
    limit: 5000,
    enabled: open,
  });

  const peopleByIndividualId = useMemo(() => {
    const map = new Map<string, Entity>();
    people.forEach((person) => {
      const rawId = (person.entity_data as PersonEntityData | undefined)?.individual_id ?? '';
      const normalized = normalizeIndividualId(rawId);
      if (!normalized || map.has(normalized)) {
        return;
      }
      map.set(normalized, person);
    });
    return map;
  }, [people]);

  const totalAssignments = useMemo(
    () => resolvedImports.reduce((sum, item) => sum + item.assignments.length, 0),
    [resolvedImports],
  );

  useEffect(() => {
    if (!open) {
      setErrorMessage(null);
      setResolvedImports([]);
      setIsImporting(false);
      setIsSaving(false);
      setFileName(null);
    }
  }, [open]);

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);
    setResolvedImports([]);
    setFileName(file.name);
    setIsImporting(true);

    try {
      if (!catalogItems.length) {
        throw new Error('Catalog data is not loaded yet. Try again in a few seconds.');
      }

      const parsed = await parseBulkPersonSizingImportFile(file, catalogItems, person.individualIdLabel);
      const unmatchedIds: string[] = [];
      const nextImports: ResolvedPersonImport[] = [];

      parsed.people.forEach((personDraft) => {
        const person = peopleByIndividualId.get(personDraft.normalizedIndividualId);
        if (!person) {
          unmatchedIds.push(personDraft.sourceIndividualId);
          return;
        }

        nextImports.push({
          person,
          individualId: personDraft.sourceIndividualId,
          assignments: Object.values(personDraft.importedDrafts).map((draft) => ({
            catalogId: draft.catalogId,
            parentId: draft.parentId,
          })),
        });
      });

      if (unmatchedIds.length > 0) {
        throw new Error(
          `No active person found for ${person.individualIdLabel}(s): ${unmatchedIds.slice(0, 5).join(', ')}${unmatchedIds.length > 5 ? ` (+${unmatchedIds.length - 5} more)` : ''}.`,
        );
      }

      await runInBatches(nextImports, 3, async (importRow) => {
        await personSizingService.validatePersonSizingAssignments(
          importRow.person.id,
          importRow.assignments,
        );
      });

      setResolvedImports(nextImports);
      notifications.success(
        'Import Validated',
        `Validated ${parsed.totalAssignments} sizing assignments across ${parsed.totalPeople} ${parsed.totalPeople === 1 ? person.lower : person.lowerPlural}.`,
      );
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      setErrorMessage(message);
      notifications.error('Import Failed', message);
    } finally {
      setIsImporting(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleSave = async () => {
    if (resolvedImports.length === 0) {
      return;
    }

    setErrorMessage(null);
    setIsSaving(true);

    try {
      await runInBatches(resolvedImports, 3, async (importRow) => {
        await personSizingService.savePersonSizingAssignments(
          importRow.person.id,
          importRow.assignments,
        );
      });

      await queryClient.invalidateQueries({ queryKey: ['entities'] });
      await queryClient.invalidateQueries({ queryKey: ['sizing'] });
      await queryClient.invalidateQueries({ queryKey: ['ie-summary'] });

      notifications.success(
        'Sizing Saved',
        `Saved sizing assignments for ${resolvedImports.length} ${resolvedImports.length === 1 ? person.lower : person.lowerPlural}.`,
      );

      setResolvedImports([]);
      onClose();
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      log.error('Failed to save bulk person sizing import', { error: String(error) });
      setErrorMessage(message);
      notifications.error('Save Failed', message);
    } finally {
      setIsSaving(false);
    }
  };

  const loadingError = catalogError || peopleError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Person Sizing Import</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a CSV/XLSX with an {person.individualIdLabel} column plus sizing columns (for example Size NSN, or
          Parent NSN + Size). The import validates all rows before saving.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Validate runs a dry-run first so you can sanity-check assignments before saving.
        </Typography>

        {loadingError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {getErrorMessage(loadingError)}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={handleImport}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            disabled={catalogLoading || peopleLoading || isImporting || isSaving}
          >
            {isImporting
              ? 'Validating Import...'
              : fileName
                ? 'Choose Another File'
                : 'Choose File'}
          </Button>
          <Button
            variant="outlined"
            component="a"
            href={PERSON_BULK_SIZING_TEMPLATE_PATH}
            download="person-bulk-sizing-template.csv"
          >
            Download Template
          </Button>
          {fileName && (
            <Typography variant="body2" color="text.secondary">
              {fileName}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Demo file for walkthroughs:{' '}
          <Typography
            component="a"
            variant="caption"
            href={PERSON_BULK_SIZING_DEMO_PATH}
            download="person-bulk-sizing-demo.csv"
            sx={{ color: 'text.secondary', textDecoration: 'underline' }}
          >
            Sample import CSV
          </Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {catalogItems.length} catalog items and {people.length} people with {person.individualIdLabel}s are loaded.
        </Typography>

        {resolvedImports.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Ready to save: {resolvedImports.length} {resolvedImports.length === 1 ? person.lower : person.lowerPlural} / {totalAssignments} assignment
              {totalAssignments === 1 ? '' : 's'}
            </Typography>
            <List
              dense
              sx={{ maxHeight: 280, overflow: 'auto', border: 1, borderColor: 'divider' }}
            >
              {resolvedImports.map((importRow) => (
                <ListItem key={`${importRow.person.id}-${importRow.individualId}`} divider>
                  <ListItemText
                    primary={importRow.person.display_name}
                    secondary={`${person.individualIdLabel}: ${importRow.individualId} • ${importRow.assignments.length} assignment${importRow.assignments.length === 1 ? '' : 's'}`}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isImporting || isSaving}>
          Cancel
        </Button>
        <ViewerGuardedButton
          variant="contained"
          onClick={handleSave}
          disabled={isImporting || isSaving || resolvedImports.length === 0}
        >
          {isSaving ? 'Saving...' : 'Save Sizing Assignments'}
        </ViewerGuardedButton>
      </DialogActions>
    </Dialog>
  );
};
